// src/modules/stock/ForecastPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    Timestamp
} from 'firebase/firestore';
import styles from './Production.module.css';

// Reusable Components
import { TableSkeleton, SearchBar, Pagination, PageHeader, EmptyState } from '../../components/common';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiDownload,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiUpload,
    FiPrinter,
    FiChevronUp,
    FiChevronDown,
    FiClock,
    FiUser,
    FiBox,
    FiDroplet,
    FiFolder,
    FiHome,
    FiBriefcase,
    FiTruck,
    FiShoppingCart,
    FiDollarSign,
    FiPercent,
    FiCreditCard,
    FiSend,
    FiFlag,
    FiCheckCircle,
    FiXCircle,
    FiClock as FiClockIcon,
    FiTrendingUp,
    FiUsers,
    FiPhone,
    FiMail,
    FiMap,
    FiNavigation,
    FiCalendar as FiCalendarIcon,
    FiMessageSquare,
    FiSettings,
    FiStar,
    FiHeart,
    FiThumbsUp,
    FiThumbsDown,
    FiMoreHorizontal,
    FiAward,
    FiDollarSign as FiMoney,
    FiArrowLeft as FiArrowLeftIcon,
    FiArrowRight as FiArrowRightIcon,
    FiPlusCircle,
    FiMinusCircle,
    FiInbox,
    FiExternalLink,
    FiHelpCircle,
    FiAlertTriangle,
    FiCheckCircle as FiCheckCircleIcon,
    FiClock as FiClockIcon2,
    FiTag as FiTagIcon,
    FiFlag as FiFlagIcon,
    FiUsers as FiUsersIcon,
    FiUser as FiUserIcon,
    FiCalendar as FiCalendarIcon2,
    FiMessageSquare as FiMessageSquareIcon,
    FiSend as FiSendIcon,
    FiBarChart2,
    FiPieChart
} from 'react-icons/fi';
import {
    MdEdit,
    MdDelete,
    MdAdd,
    MdSave,
    MdCancel,
    MdVisibility,
    MdPrint,
    MdLocationOn,
    MdBusiness,
    MdPerson,
    MdPhone,
    MdEmail,
    MdAttachMoney,
    MdReceipt,
    MdLocalShipping,
    MdCheckCircle,
    MdCancel as MdCancelIcon,
    MdPending,
    MdApproval,
    MdProductionQuantityLimits,
    MdDeliveryDining,
    MdFactCheck,
    MdMoney,
    MdMonetizationOn,
    MdOutlineCheckCircle,
    MdOutlineCancel,
    MdOutlinePending,
    MdInventory,
    MdHelp,
    MdSupport,
    MdAssignment,
    MdPeople,
    MdPersonOutline,
    MdCategory,
    MdPriorityHigh,
    MdAccessTime,
    MdAssignmentInd,
    MdQrCode,
    MdPrint as MdPrintIcon,
    MdShowChart,
    MdTimeline
} from 'react-icons/md';
import { FaBox, FaTruck, FaUser, FaBuilding, FaMoneyBillWave, FaClipboardList, FaDollarSign, FaPrint, FaCheck, FaTimes, FaEye, FaMapMarkerAlt, FaBoxOpen, FaWarehouse, FaTicketAlt, FaClock, FaFlag, FaUserTag, FaUsers, FaTasks, FaBarcode, FaQrcode, FaChartLine, FaChartBar } from 'react-icons/fa';

// ============================================================================
// CONSTANTS
// ============================================================================
const STOCK_OUTFLOWS_COLLECTION = 'stock-outflows';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const ITEMS_PER_PAGE = 20;

const MONTHS = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

// Forecast method options
const FORECAST_METHODS = {
    AVERAGE: 'average',
    WEIGHTED: 'weighted',
    LAST_MONTH: 'last_month',
    SAME_MONTH_LAST_YEAR: 'same_month'
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const getCurrentYear = () => new Date().getFullYear();
const getCurrentMonth = () => new Date().getMonth();

const getHistoryMonths = () => {
    const currentYear = getCurrentYear();
    const currentMonth = getCurrentMonth();
    const months = [];

    for (let month = 0; month < 12; month++) {
        months.push({ year: currentYear - 1, month, label: `${MONTHS[month]}/${currentYear - 1}` });
    }

    for (let month = 0; month <= currentMonth; month++) {
        months.push({ year: currentYear, month, label: `${MONTHS[month]}/${currentYear}` });
    }

    return months;
};

const getForecastMonths = () => {
    const currentYear = getCurrentYear();
    const currentMonth = getCurrentMonth();
    const months = [];

    for (let month = currentMonth; month < 12; month++) {
        months.push({ year: currentYear, month, label: `${MONTHS[month]}/${currentYear}` });
    }

    return months;
};

const calculateForecast = (historicalData, targetYear, targetMonth, method = FORECAST_METHODS.AVERAGE) => {
    const lastThreeMonths = [];
    for (let i = 1; i <= 3; i++) {
        const prevDate = new Date(targetYear, targetMonth - i, 1);
        const prevYear = prevDate.getFullYear();
        const prevMonth = prevDate.getMonth();
        const key = `${prevYear}-${prevMonth}`;
        const value = historicalData[key] || 0;
        lastThreeMonths.push(value);
    }

    const sameMonthLastYear = historicalData[`${targetYear - 1}-${targetMonth}`] || 0;

    switch (method) {
        case FORECAST_METHODS.WEIGHTED:
            const weights = [0.5, 0.3, 0.2];
            return lastThreeMonths.reduce((sum, val, idx) => sum + val * weights[idx], 0);

        case FORECAST_METHODS.LAST_MONTH:
            return lastThreeMonths[0] || 0;

        case FORECAST_METHODS.SAME_MONTH_LAST_YEAR:
            return sameMonthLastYear;

        case FORECAST_METHODS.AVERAGE:
        default:
            const validMonths = lastThreeMonths.filter(v => v > 0);
            if (validMonths.length === 0) return 0;
            return validMonths.reduce((a, b) => a + b, 0) / validMonths.length;
    }
};

const sortByCode = (materials) => {
    return [...materials].sort((a, b) => {
        const codeA = a.code || '';
        const codeB = b.code || '';
        return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
    });
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ForecastPage = () => {
    const { t } = useTranslation();
    // Mode: 'history' or 'forecast'
    const [mode, setMode] = useState('history');

    // Forecast method (only used in forecast mode)
    const [forecastMethod, setForecastMethod] = useState(FORECAST_METHODS.AVERAGE);

    // Data states
    const [rawMaterials, setRawMaterials] = useState([]);
    const [historicalDataMap, setHistoricalDataMap] = useState({});
    const [consumptionData, setConsumptionData] = useState({});
    const [loading, setLoading] = useState(true);

    // Search and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Get months based on mode
    const months = useMemo(() => {
        return mode === 'history' ? getHistoryMonths() : getForecastMonths();
    }, [mode]);

    // ============================================================================
    // FETCH RAW MATERIALS (only raw-materials, no products)
    // ============================================================================
    useEffect(() => {
        const fetchRawMaterials = async () => {
            setLoading(true);
            try {
                const rawMatsSnapshot = await getDocs(collection(db, RAW_MATERIALS_COLLECTION));

                const allRawMaterials = rawMatsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    code: doc.data().code,
                    name: doc.data().commercialName || doc.data().name || doc.data().inciName,
                    type: t('pages.forecast.rawMaterial')
                }));

                const sortedMaterials = sortByCode(allRawMaterials);
                setRawMaterials(sortedMaterials);
            } catch (error) {
                console.error('Error fetching raw materials:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRawMaterials();
    }, [t]);

    // ============================================================================
    // FETCH HISTORICAL CONSUMPTION DATA
    // ============================================================================
    useEffect(() => {
        const fetchHistoricalConsumption = async () => {
            if (rawMaterials.length === 0) return;

            setLoading(true);

            try {
                const startDate = new Date(getCurrentYear() - 1, 0, 1);
                const endDate = new Date(getCurrentYear(), 11, 31);

                const startTimestamp = Timestamp.fromDate(startDate);
                const endTimestamp = Timestamp.fromDate(endDate);

                const q = query(
                    collection(db, STOCK_OUTFLOWS_COLLECTION),
                    where('createdAt', '>=', startTimestamp),
                    where('createdAt', '<=', endTimestamp),
                    orderBy('createdAt', 'asc')
                );

                const snapshot = await getDocs(q);

                const historyMap = {};

                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    const materialId = data.rawMaterialId;
                    const quantity = data.quantity || 0;
                    const date = data.createdAt?.toDate();

                    if (!materialId || !date) return;

                    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

                    if (!historyMap[materialId]) {
                        historyMap[materialId] = {};
                    }

                    historyMap[materialId][monthKey] = (historyMap[materialId][monthKey] || 0) + quantity;
                });

                setHistoricalDataMap(historyMap);

            } catch (error) {
                console.error('Error fetching historical consumption:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchHistoricalConsumption();
    }, [rawMaterials]);

    // ============================================================================
    // BUILD CONSUMPTION DATA (HISTORY OR FORECAST)
    // ============================================================================
    useEffect(() => {
        if (rawMaterials.length === 0) return;

        const buildData = () => {
            const dataMap = {};

            rawMaterials.forEach(material => {
                const materialHistory = historicalDataMap[material.id] || {};

                dataMap[material.id] = {
                    id: material.id,
                    code: material.code,
                    name: material.name,
                    monthlyData: {}
                };

                if (mode === 'history') {
                    months.forEach(month => {
                        const key = `${month.year}-${month.month}`;
                        dataMap[material.id].monthlyData[key] = materialHistory[key] || 0;
                    });
                } else {
                    months.forEach(month => {
                        const key = `${month.year}-${month.month}`;
                        const forecastValue = calculateForecast(
                            materialHistory,
                            month.year,
                            month.month,
                            forecastMethod
                        );
                        dataMap[material.id].monthlyData[key] = forecastValue;
                    });
                }
            });

            setConsumptionData(dataMap);
        };

        buildData();
    }, [rawMaterials, historicalDataMap, mode, months, forecastMethod]);

    // ============================================================================
    // FILTERED AND SORTED MATERIALS
    // ============================================================================
    const filteredMaterials = useMemo(() => {
        const term = searchTerm.toLowerCase();
        const filtered = Object.values(consumptionData).filter(m =>
            !term ||
            (m.code || '').toLowerCase().includes(term) ||
            (m.name || '').toLowerCase().includes(term)
        );
        return sortByCode(filtered);
    }, [consumptionData, searchTerm]);

    // ============================================================================
    // PAGINATION
    // ============================================================================
    const totalPages = Math.ceil(filteredMaterials.length / ITEMS_PER_PAGE);
    const paginatedMaterials = filteredMaterials.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, mode, forecastMethod]);

    const getMonthKey = (year, month) => `${year}-${month}`;

    // ============================================================================
    // RENDER FORECAST METHOD SELECTOR
    // ============================================================================
    const renderForecastSelector = () => {
        if (mode !== 'forecast') return null;

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px',
                padding: '12px 16px',
                background: 'var(--primary-light)',
                borderRadius: 'var(--border-radius-md)',
                flexWrap: 'wrap'
            }}>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}><FiTrendingUp size={16} style={{ marginRight: '6px' }} /> {t('pages.forecast.methodLabel')}</span>
                <select
                    value={forecastMethod}
                    onChange={(e) => setForecastMethod(e.target.value)}
                    className={styles.select}
                    style={{ width: 'auto', minWidth: '220px' }}
                >
                    <option value={FORECAST_METHODS.AVERAGE}>{t('pages.forecast.methodAverage')}</option>
                    <option value={FORECAST_METHODS.WEIGHTED}>{t('pages.forecast.methodWeighted')}</option>
                    <option value={FORECAST_METHODS.LAST_MONTH}>{t('pages.forecast.methodLastMonth')}</option>
                    <option value={FORECAST_METHODS.SAME_MONTH_LAST_YEAR}>{t('pages.forecast.methodSameMonth')}</option>
                </select>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                    <FiInfo size={12} style={{ marginRight: '4px' }} /> {t('pages.forecast.methodHint')}
                </span>
            </div>
        );
    };

    // ============================================================================
    // RENDER
    // ============================================================================
    return (
        <div className={styles.pageContainer}>
            {/* Page Header - Using reusable PageHeader component */}
            <PageHeader
                title={t('pages.forecast.title')}
                showAction={false}
            />
            <p style={{ color: 'var(--text-secondary)', marginTop: '-16px', marginBottom: '24px' }}>
                {t('pages.forecast.subtitle')}
            </p>

            {/* Mode Toggle */}
            <div style={{
                display: 'flex',
                gap: '0',
                marginBottom: '24px',
                background: 'var(--content-bg)',
                borderRadius: 'var(--border-radius-lg)',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                width: 'fit-content'
            }}>
                <button
                    className={mode === 'history' ? styles.addButton : styles.cancelButton}
                    onClick={() => setMode('history')}
                    style={{
                        borderRadius: 0,
                        margin: 0,
                        padding: '10px 24px'
                    }}
                >
                    <FiClock size={16} style={{ marginRight: '6px' }} /> {t('pages.forecast.history')}
                </button>
                <button
                    className={mode === 'forecast' ? styles.addButton : styles.cancelButton}
                    onClick={() => setMode('forecast')}
                    style={{
                        borderRadius: 0,
                        margin: 0,
                        padding: '10px 24px'
                    }}
                >
                    <FiTrendingUp size={16} style={{ marginRight: '6px' }} /> {t('pages.forecast.forecast')}
                </button>
            </div>

            {/* Forecast Method Selector */}
            {renderForecastSelector()}

            {/* Search and Results Count - Using reusable SearchBar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <SearchBar
                    placeholder={t('pages.forecast.searchPlaceholder')}
                    onSearch={(value) => setSearchTerm(value)}
                    debounceDelay={300}
                    initialValue={searchTerm}
                />
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table} style={{ minWidth: `${600 + months.length * 100}px` }}>
                        <thead>
                            <tr>
                                <th style={{ minWidth: '100px', position: 'sticky', left: 0, background: 'var(--header-bg)' }}>{t('pages.forecast.tableCode')}</th>
                                <th style={{ minWidth: '250px' }}>{t('pages.forecast.tableMaterial')}</th>
                                {months.map((month, idx) => (
                                    <th key={idx} style={{ textAlign: 'center', minWidth: '100px' }}>
                                        {month.label}
                                    </th>
                                ))}
                                <th style={{ textAlign: 'center', minWidth: '100px' }}>{t('pages.forecast.tableTotal')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton rows={ITEMS_PER_PAGE} columns={months.length + 3} />
                            ) : paginatedMaterials.length === 0 ? (
                                <tr>
                                    <td colSpan={months.length + 3} className={styles.emptyTable}>
                                        <EmptyState
                                            title={t('pages.forecast.emptyTitle')}
                                            message={t('pages.forecast.empty')}
                                        />
                                    </td>
                                </tr>
                            ) : (
                                paginatedMaterials.map((material) => {
                                    let total = 0;
                                    months.forEach(month => {
                                        const key = getMonthKey(month.year, month.month);
                                        total += material.monthlyData[key] || 0;
                                    });

                                    return (
                                        <tr key={material.id} className={styles.tableRow}>
                                            <td style={{ position: 'sticky', left: 0, background: 'var(--content-bg)', fontWeight: 'bold' }}>
                                                {material.code || '—'}
                                            </td>
                                            <td>{material.name || '—'}</td>
                                            {months.map((month, idx) => {
                                                const key = getMonthKey(month.year, month.month);
                                                const value = material.monthlyData[key] || 0;
                                                return (
                                                    <td key={idx} style={{ textAlign: 'center' }}>
                                                        {value > 0 ? value.toFixed(2) : '-'}
                                                    </td>
                                                );
                                            })}
                                            <td style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: 'var(--primary-light)' }}>
                                                {total.toFixed(2)}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination - Using reusable Pagination component */}
                {!loading && totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={filteredMaterials.length}
                        onPageChange={setCurrentPage}
                        itemsLabel={t('pages.forecast.items')}
                    />
                )}
            </div>
        </div>
    );
};

export default ForecastPage;